import { computed, type ComputedRef } from 'vue'
import { useLocale } from '@render/composables/useLocale'
import { isRtlLanguage } from '@render/lib/isRtlLanguage'

/**
 * Reactive text direction for the current pipe — the single place components
 * should ask "is this RTL?".
 *
 * Direction is derived from the active language code (`isRtlLanguage(language)`),
 * which is the source of truth in BOTH contexts and avoids a stale-flag trap:
 * - Built (STATIC) pipes: `system.language` is the distribution language code,
 *   set synchronously in `usePipe.initialize`, so this is SSR-correct.
 * - Editor preview: `system.language` is updated on every language switch
 *   (`setTranslations`), so direction flips live. We deliberately do NOT read
 *   `const.system.isRtl` here: the editor seeds it `false` at init, so a boolean
 *   is always present and a "trust the boolean" rule would mask the language —
 *   reporting LTR until/unless the flag is refreshed. `system.isRtl` /
 *   `var.global.isRtl` still exist for author-facing visibility rules; they're
 *   computed from the same code list, so they agree with this.
 *
 * SSR-safe (pure `computed` read) and degrades to `ltr` outside a pipe context
 * (via `useLocale`, which defaults the language to `en`).
 */
export function useDirection() {
  const { language } = useLocale()

  const isRtl = computed<boolean>(() => isRtlLanguage(language.value))
  const dir = computed<'rtl' | 'ltr'>(() => (isRtl.value ? 'rtl' : 'ltr'))

  return { isRtl, dir }
}

export type UseDirectionReturn = ReturnType<typeof useDirection>
export type DirectionRef = ComputedRef<'rtl' | 'ltr'>
